/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";

// Material-UI components
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import apiClient from "./../../../../apiClient";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import DataTable from "examples/Tables/DataTable";
import MDInput from "components/MDInput";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

const FetchCityData = ({ onRefresh, editData }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stateInput, setStateInput] = useState("");
  const [districtInput, setDistrictInput] = useState("");
  const [cityInput, setCityInput] = useState("");
  const [statusInput, setStatusInput] = useState("Active");
  const [stateFilter, setStateFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Fetch states data
  const fetchStates = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get("/admin/cites");
      setData(response.data);
    } catch (error) {
      console.error("Error fetching city data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStates();
  }, [onRefresh]);

  // Handlers
  const handleActivate = async (id, status) => {
    try {
      await apiClient.put(`/admin/cites/${id}`, { status: status });
      fetchStates(); // Refresh data after status update
    } catch (error) {
      console.error("Error activating city:", error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/admin/cites/${id}`);
      fetchStates(); // Refresh data after delete
    } catch (error) {
      console.error("Error deleting city:", error);
    }
  };

  const handleEdit = (id) => {
    // Handle edit functionality
    console.log("Edit state with ID:", id);
    editData(id);
  };

  const handleStateInputChange = (e) => {
    setStateInput(e.target.value);
  };

  const handleDistrictInputChange = (e) => {
    setDistrictInput(e.target.value);
  };

  const handleCityInputChange = (e) => {
    setCityInput(e.target.value);
  };

  const handleStatusInputChange = (e) => {
    setStatusInput(e.target.value);
  };

  const handleFilterSubmit = () => {
    setStateFilter(stateInput);
    setStatusFilter(statusInput);
    setDistrictFilter(districtInput);
    setCityFilter(cityInput);
  };

  // Filter the rows based on search term
  const filteredRows = data.filter((row) => {
    return (
      (stateFilter === "" || row.state.toLowerCase().includes(stateFilter.toLowerCase())) &&
      (districtFilter === "" ||
        row.district.toLowerCase().includes(districtFilter.toLowerCase())) &&
      (cityFilter === "" || row.name.toLowerCase().includes(cityFilter.toLowerCase())) &&
      (statusFilter === "" || row.status.toLowerCase().includes(statusFilter.toLowerCase()))
    );
  });

  // Table columns and rows
  const columns = [
    { Header: "State", accessor: "state", width: "25%", align: "left", isSorted: false },
    { Header: "District", accessor: "district", align: "center", isSorted: false },
    { Header: "City", accessor: "name", align: "center", isSorted: true },
    { Header: "Status", accessor: "status", align: "center", isSorted: true },
    { Header: "Action", accessor: "action", align: "center", isSorted: false },
  ];

  const rows = filteredRows.map((item) => ({
    state: (
      <MDTypography component="span" variant="caption" color="text" fontWeight="medium">
        {item.state}
      </MDTypography>
    ),
    district: (
      <MDTypography component="span" variant="caption" color="text" fontWeight="medium">
        {item.district}
      </MDTypography>
    ),
    name: (
      <MDTypography component="span" variant="caption" color="text" fontWeight="medium">
        {item.name}
      </MDTypography>
    ),
    status: (
      <MDBox ml={-1}>
        <MDBadge
          badgeContent={item.status}
          color={item.status === "Active" ? "success" : "warning"}
          variant="gradient"
          size="sm"
        />
      </MDBox>
    ),
    action: (
      <MDBox display="flex" justifyContent="space-around">
        <IconButton onClick={() => handleEdit(item)} color="secondary">
          <EditIcon />
        </IconButton>
        <IconButton
          onClick={() => handleActivate(item._id, item.status)}
          color={item.status === "Active" ? "warning" : "success"}
        >
          <CheckCircleIcon />
        </IconButton>
        <IconButton onClick={() => handleDelete(item._id)} color="error">
          <DeleteIcon />
        </IconButton>
      </MDBox>
    ),
  }));

  // No data message
  if (loading) return <div>Loading...</div>;
  if (data.length === 0) return <div>No data found.</div>;

  const handleClearFilters = () => {
    setStateFilter("");
    setDistrictFilter("");
    setStatusFilter("");
    setStateInput("");
    setDistrictInput("");
    setCityInput("");
    setCityFilter("");
  };

  return (
    <div>
      <MDBox
        display="flex"
        gap="1rem"
        mb="1rem"
        justifyContent="left"
        alignItems="left"
        ml="2%"
        width="90%"
      >
        <MDInput
          fullWidth
          label="State"
          variant="outlined"
          value={stateInput}
          onChange={handleStateInputChange}
        />
        <MDInput
          fullWidth
          label="District"
          variant="outlined"
          value={districtInput}
          onChange={handleDistrictInputChange}
        />
        <MDInput
          fullWidth
          label="City"
          variant="outlined"
          value={cityInput}
          onChange={handleCityInputChange}
        />
        <Select
          fullWidth
          variant="outlined"
          value={statusInput}
          onChange={handleStatusInputChange}
          displayEmpty
        >
          <MenuItem value="">
            <em>Status</em>
          </MenuItem>
          <MenuItem value="Active">Active</MenuItem>
          <MenuItem value="Inactive">Inactive</MenuItem>
        </Select>
        <Button
          variant="contained"
          color="primary"
          onClick={handleFilterSubmit}
          sx={{ color: "#fff" }}
        >
          Search
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleClearFilters}
          sx={{ color: "#fff" }}
        >
          Clear
        </Button>
      </MDBox>
      <Divider />
      <DataTable
        table={{ columns, rows }}
        isSorted={true}
        entriesPerPage={true}
        showTotalEntries={true}
        noEndBorder
      />
    </div>
  );
};
FetchCityData.propTypes = {
  onRefresh: PropTypes.func.isRequired,
  editData: PropTypes.func.isRequired,
};

export default FetchCityData;
